import type { TFunction } from "i18next";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { sentenceCase, spaced } from "../../../api/content/processTermWords";
import { Link } from "react-router";
import type { HumanInputPayload, HumanStepRequestedPayload, StructuredOutput, ThreadEntry } from "../../../api/threads/liveMessages";
import { answerHumanStep, newClientKey, type HumanInputRequest } from "../../../api/threads/threadsApi";
import { appPaths } from "../../../shared/routing/appPaths";
import { clearPreparedAnswer, readPreparedAnswer } from "../prepare/preparedAnswer";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import MarkdownRenderer from "../../../ui/content/MarkdownRenderer";
import SourceView from "../../../ui/content/source/SourceView";
import Field from "../../../ui/forms/Field";
import FieldGroup from "../../../ui/forms/FieldGroup";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { answerProblems, NO_ANSWER_PROBLEMS, sameAnnotations } from "../../annotation/annotationEditing";
import AnnotationSetView from "../../annotation/AnnotationSetView";
import { useAnnotationSetEditing } from "../../annotation/useAnnotationSetEditing";
import { useProjects } from "../../../projects/projectContext";
import BlockedState from "../../../ui/feedback/BlockedState";
import { releaseDecision } from "../../knowledgeData/releases/releasePermission";
import ReleasePartAnswer from "../../knowledgeData/releases/ReleasePartAnswer";
import { readReleaseRequest, type ReleasePart } from "../../knowledgeData/releases/releaseRequest";
import ReleaseRequestSummary from "../../knowledgeData/releases/ReleaseRequestSummary";
import { showCurrentStep } from "../page/currentStep/currentStepElement";
import { useThreadContext } from "../page/threadPageContext";
import { actionDecision } from "../page/viewerActions";
import { requestWaitText, workflowLeftText } from "../status/requestWaitWords";
import TalkAnswer from "../talk/TalkAnswer";
import TalkWithAgent from "../talk/TalkWithAgent";
import { requestTranslation, responseDefinition, reviewedInputOf, type HumanStepInput } from "./humanStepResponse";
import { ProposedInstanceData } from "./InstanceDataOutput";

/**
 * Human steps (workflows.md W-11): the frozen instructions and inputs in the log, and, while
 * the step waits, the answer in the current step's place (F33; one editor per request): a
 * comment, a choice, or the reviewed result, confirmed or edited. A
 * review of annotations edits them in v1's span editor; the person's set is what the
 * Workflow proposes (decision.dmw.review-makes-the-proposal).
 */
export function HumanStepRequestedBody({ entry, payload }: { entry: ThreadEntry; payload: HumanStepRequestedPayload }) {
  const { t, i18n } = useTranslation();
  const instructions = requestTranslation(payload, i18n.resolvedLanguage)?.instructions ?? payload.instructions;
  const { overview, nameOf, viewerIsHost, openHumanStepEntry, requestInStep, supersededRequests, currentStep } = useThreadContext();
  const waiting = openHumanStepEntry === entry.number ? (overview.projection.open_human_step ?? null) : null;
  // Module C's decision for this viewer on this request (W-22 slice 2).
  const answering = actionDecision(overview.viewer, "answer_request", String(entry.number));
  const mayAnswer = answering.allowed;
  // The current step shows this request's inputs where it is answered: shown once (F104).
  const inStep = requestInStep === entry.number;
  const superseded = supersededRequests.has(entry.number);
  const hostName = viewerIsHost ? null : nameOf(overview.host);
  // While it waits and no step shows it (W-22 slice 1): once the Workflow was left, who brings it back, for everyone; else
  // whom it waits for, or where it is answered. Shown in the current step, the step says it, once (IM-THREAD).
  const waitLine =
    waiting === null || inStep ? null
    : waiting.workflow_left === true ? workflowLeftText(hostName, t)
    : !mayAnswer ? requestWaitText(waiting, answering.refusal, hostName, t)
    : t("threads.humanStep.answerInCurrentStep");
  return (
    <div className="space-y-2">
      {/* Shown in the current step, its instructions are said there, as its inputs are (F104; run-frame P1). */}
      {inStep ? null : (
        <>
          <p className="app-text-muted text-xs">{t("threads.humanStep.requested")}</p>
          <MarkdownRenderer content={instructions} className="app-markdown text-sm" />
        </>
      )}
      {superseded ? <p className="app-text-muted text-xs">{t("threads.humanStep.superseded")}</p> : null}
      {inStep ? (
        <button type="button" className="app-link text-xs underline" onClick={() => { showCurrentStep(currentStep); }}>
          {t(mayAnswer ? "threads.humanStep.answerInCurrentStep" : "threads.humanStep.inCurrentStep")}
        </button>
      ) : (
        <HumanStepInputs inputs={payload.inputs} />
      )}
      {waitLine === null ? null : <p className="app-text-muted text-xs">{waitLine}</p>}
    </div>
  );
}

/**
 * An input in words (P-F): what it holds where that is a kind of result, such as "Instance data", else its slot's
 * name spaced, never its key.
 */
function inputName(input: HumanStepInput, t: TFunction): string {
  const kind = input.value?.output_kind;
  return kind === undefined || kind === "value" ? sentenceCase(spaced(input.slot)) : t(`threads.toolTurn.valueKinds.${kind}`);
}

/** Each input, folded unless it is the only one; `edited` draws one slot's input in its place. */
export function HumanStepInputs({ inputs, edited }: { inputs: readonly HumanStepInput[]; edited?: { slot: string; view: ReactNode } }) {
  const { t } = useTranslation();
  return inputs.map((input) => (
    <details key={input.slot} open={inputs.length === 1 || input.slot === edited?.slot}>
      <summary className="app-text-muted cursor-pointer text-xs">
        {inputName(input, t)}
        {input.from_entry === null ? null : ` · #${String(input.from_entry)}`}
      </summary>
      {input.slot === edited?.slot ? edited.view : <HumanStepInputView input={input} />}
    </details>
  ));
}

/** A tool's text (such as a converter's findings) reads as Markdown; instance data in its one
 * view, the regest (F154); a change set links to its review; everything else as the text the
 * server rendered. */
function HumanStepInputView({ input }: { input: HumanStepInput }) {
  const { t } = useTranslation();
  const { overview, threadId } = useThreadContext();
  const value = input.value;
  if (value?.output_kind === "value" && value.value_kind === "text" && typeof value.value === "string") {
    return <MarkdownRenderer content={value.value} className="app-markdown app-surface-muted mt-1 max-h-96 overflow-auto rounded p-2 text-sm" />;
  }
  if (value?.output_kind === "annotations") {
    return <AnnotationSetView output={value} />;
  }
  if (value?.output_kind === "instance_data") {
    return <ProposedInstanceData set={value} entry={input.from_entry === null ? null : `${threadId}#${String(input.from_entry)}`} projectId={overview.projectId} />;
  }
  const release = readReleaseRequest(value);
  if (release !== null) {
    // The summary leads; exact references remain available on request.
    return (
      <div className="mt-1 space-y-1">
        <ReleaseRequestSummary projectId={overview.projectId} request={release} />
        <details>
          <summary className="app-link cursor-pointer text-xs">{t("threads.turn.details")}</summary>
          <SourceView label={inputName(input, t)} language="plaintext" value={input.text} />
        </details>
      </div>
    );
  }
  if (value?.output_kind === "change_set") {
    const path = value.collection === "corpus" ? appPaths.importProposal(value.change_set_id) : appPaths.review(value.change_set_id);
    return (
      <p className="mt-1 text-sm">
        <Link to={path} className="app-link underline">{t("threads.output.openReview")}</Link>
      </p>
    );
  }
  return <SourceView label={inputName(input, t)} language="plaintext" value={input.text} className="mt-1" />;
}

/** The answer to the person step that waits: the current step shows it (F33). A release
 * step's answer is its part of the release, in that part's form. */
export function HumanStepAnswer({ entry, payload }: { entry: ThreadEntry; payload: HumanStepRequestedPayload }) {
  const definition = responseDefinition(payload.response);
  return definition.kind === "release_part" ? <ReleaseStepAnswer entry={entry} payload={payload} part={definition.part} /> : <ResponseAnswer entry={entry} payload={payload} />;
}

function ReleaseStepAnswer({ entry, payload, part }: { entry: ThreadEntry; payload: HumanStepRequestedPayload; part: ReleasePart }) {
  const { threadId, overview } = useThreadContext();
  return (
    <BlockedState decision={releaseDecision(useProjects(), overview.projectId)}>
      <div className="app-surface-muted rounded p-3">
        {/* Each request its own form: the next part's request may arrive with this answer. */}
        <ReleasePartAnswer key={entry.number} projectId={overview.projectId} threadId={threadId} answersEntry={entry.number} part={part} earlier={readReleaseRequest(reviewedInputOf(payload)?.value)} />
      </div>
    </BlockedState>
  );
}

function ResponseAnswer({ entry, payload }: { entry: ThreadEntry; payload: HumanStepRequestedPayload }) {
  const { t, i18n } = useTranslation();
  const choiceLabels = requestTranslation(payload, i18n.resolvedLanguage)?.choice_labels ?? null;
  const { threadId } = useThreadContext();
  const definition = responseDefinition(payload.response);
  const [comment, setComment] = useState(() => readPreparedAnswer(threadId) ?? "");
  const [choice, setChoice] = useState<string | null>(null);
  const [workspaceFile, setWorkspaceFile] = useState("");
  const [clientRequestId] = useState(newClientKey);
  const { state, submit, reset } = useSubmission();
  const reviewed = reviewedInputOf(payload)?.value ?? null;
  // An edit changes what would be sent: a refusal of the earlier set no longer applies.
  const failed = state.status === "failed";
  const forgetRefusal = useCallback(() => { if (failed) reset(); }, [failed, reset]);
  // Annotations are edited in the span editor; instance data, change sets and a tool's value
  // (such as a release request) are confirmed as they are.
  const review = useAnnotationSetEditing(reviewed?.output_kind === "annotations" ? reviewed : null, forgetRefusal);
  const result = review?.edited ?? reviewed;
  const problems = useMemo(() => (state.status === "failed" ? answerProblems(state.error) : NO_ANSWER_PROBLEMS), [state]);
  const commentMissing = (definition.kind === "text" || (definition.kind === "review_output" && definition.commentRequired)) && comment.trim() === "";
  const fileMissing = definition.kind === "workspace_file" && !fitsSuffixes(workspaceFile.trim(), definition.suffixes);
  const ready = !commentMissing && !fileMissing && (definition.kind !== "choice" || choice !== null);
  const settled = state.status === "submitting" || state.status === "succeeded";

  const send = () => {
    submit(async () => {
      await answerHumanStep(threadId, {
        answersEntry: entry.number,
        comment: comment.trim() === "" ? null : comment.trim(),
        choice: definition.kind === "choice" ? choice : null,
        result: result === null ? null : sentResult(result),
        workspaceFile: definition.kind === "workspace_file" ? workspaceFile.trim() : null,
        clientRequestId,
      });
      review?.markSent();
      clearPreparedAnswer(threadId);
    });
  };

  const editedView = review === null || definition.kind !== "review_output" ? undefined : {
    slot: definition.input,
    view: <AnnotationSetView output={review.edited} editing={settled ? undefined : review.editor} problems={problems} />,
  };
  return (
    <>
      {payload.answered_talk === null || payload.answered_talk === undefined ? null : <TalkAnswer talk={payload.answered_talk} />}
      <HumanStepInputs inputs={payload.inputs} edited={editedView} />
      <div className="app-surface-muted space-y-2 rounded p-3">
        {definition.kind === "choice" ? (
          <FieldGroup legend={t("threads.humanStep.choose")}>
            {definition.options.map((option) => (
              <OptionLabel key={option.key}>
                <input type="radio" name={`choice-${String(entry.number)}`} checked={choice === option.key} onChange={() => { setChoice(option.key); }} />
                {choiceLabels?.[option.key] ?? option.label}
              </OptionLabel>
            ))}
          </FieldGroup>
        ) : null}
        {definition.kind === "review_output" ? (
          <p className="app-text text-xs">{t(review === null ? "threads.humanStep.reviewHint" : "threads.humanStep.reviewAnnotationsHint")}</p>
        ) : null}
        {definition.kind === "workspace_file" ? (
          <Field label={t("threads.humanStep.workspaceFile", { suffixes: definition.suffixes.join(", ") })} hint={t("threads.humanStep.workspaceFileHint")}>
            {(control) => (
              <input {...control} className={FORM_CONTROL_CLASS} value={workspaceFile} placeholder="my-corpus/document.pdf" onChange={(event) => { setWorkspaceFile(event.target.value); }} />
            )}
          </Field>
        ) : null}
        <Field label={commentMissing ? t("threads.humanStep.commentRequired") : t("threads.humanStep.comment")}>
          {(control) => (
            <textarea {...control} className={FORM_CONTROL_CLASS} rows={3} value={comment} onChange={(event) => { setComment(event.target.value); }} />
          )}
        </Field>
        {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
        <div className="flex flex-wrap gap-2">
          <AppButton variant="primary" size="sm" disabled={!ready || settled} onClick={send}>
            {definition.kind !== "review_output" ? t("threads.humanStep.send") : review?.changed === true ? t("threads.humanStep.submitEdited") : t("threads.humanStep.confirm")}
          </AppButton>
          {review?.changed === true && !settled ? (
            <AppButton variant="secondary" size="sm" onClick={review.discard}>
              {t("editing.discard")}
            </AppButton>
          ) : null}
        </div>
        {definition.kind === "review_output" && definition.talk ? (
          <TalkWithAgent
            threadId={threadId}
            reviewEntry={entry.number}
            reviewed={reviewed?.output_kind ?? null}
            result={review?.changed === true && result !== null ? sentResult(result) : null}
            disabled={settled}
            onSent={() => { review?.markSent(); }}
          />
        ) : null}
      </div>
    </>
  );
}

/** The result as the answer sends it, with the defaults the server's body expects. */
function sentResult(result: StructuredOutput): HumanInputRequest["result"] {
  switch (result.output_kind) {
    case "change_set":
      return { ...result, collection: result.collection ?? "project_content" };
    case "value":
      return result;
    case "annotations":
      return { ...result, documents: result.documents ?? [] };
    case "instance_data":
      // The server leaves an explicit claim out of what it sends (R6), and its body names one.
      return {
        ...result,
        documents: result.documents ?? [],
        statements: result.statements.map((statement) => ({ ...statement, claim: statement.claim ?? "explicit" })),
      };
  }
}

/** A workspace path the server will take: relative, with `/`, ending with an allowed suffix. */
function fitsSuffixes(path: string, suffixes: readonly string[]): boolean {
  if (path === "" || path.startsWith("/") || path.includes("\\") || path.split("/").some((part) => part === "" || part === "." || part === "..")) {
    return false;
  }
  return suffixes.length === 0 || suffixes.some((suffix) => path.toLowerCase().endsWith(suffix));
}

/**
 * An answer, and the result it sent: an annotation set shows in the span view, marked
 * confirmed or edited against the set the step reviewed (as v1 showed the reviewed
 * result). Without the request in the loaded log, it only says that a result was sent.
 */
export function HumanInputBody({ payload, reviewed, request }: { payload: HumanInputPayload; reviewed?: StructuredOutput; request?: ThreadEntry["payload"] }) {
  const { t, i18n } = useTranslation();
  const asked = request?.kind === "human_step_requested" ? request : null;
  const choices = asked === null ? null : responseDefinition(asked.response);
  const labels = asked === null ? null : (requestTranslation(asked, i18n.resolvedLanguage)?.choice_labels ?? null);
  const choice = (payload.choice !== null ? labels?.[payload.choice] : null) ?? (choices?.kind === "choice" ? choices.options.find((option) => option.key === payload.choice)?.label : null) ?? null;
  const result = payload.result;
  const edited = result?.output_kind === "annotations" && reviewed?.output_kind === "annotations" ? !sameAnnotations(reviewed, result) : null;
  const resultNote = edited === null ? t("threads.humanStep.sentResult") : edited ? t("threads.humanStep.editedResult") : t("threads.humanStep.confirmedResult");
  return (
    <div className="space-y-1 text-sm">
      <p className="app-text-muted text-xs">
        {t("threads.humanStep.answered", { entry: payload.answers_entry })}
        {choice === null ? null : ` · ${t("threads.humanStep.chose", { choice })}`}
        {result === null ? null : ` · ${resultNote}`}
      </p>
      {payload.comment === null ? null : <MarkdownRenderer content={payload.comment} className="app-markdown text-sm" />}
      {result?.output_kind === "annotations" ? (
        // A confirmed set is the one the request shows; an edited one opens, since it differs.
        <details open={edited !== false}>
          <summary className="app-text-muted cursor-pointer text-xs">{t("threads.humanStep.sentSet")}</summary>
          <AnnotationSetView output={result} />
        </details>
      ) : null}
    </div>
  );
}
